/* Full-page Pending Worklist for one service bucket (opened in a new tab from Pending by Service). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Search } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { ParcelQueryDialog } from "@/components/ParcelQueryDialog";
import { BackBar, openPage } from "@/components/BackBar";
import { AGE_LABEL, psrParams, ParcelTable, QueryFilterSelect, applyQueryFilter, ReasonFilterSelect, reasonMatch } from "@/components/PendingServiceReport";

const REFRESH_MS = 60000;
const NOTE_FILTERS = [["", "All parcels"], ["notes", "Has notes"], ["hl", "Any highlight"], ["yellow", "Yellow"],
  ["green", "Green"], ["red", "Red"], ["blue", "Blue"], ["none", "No notes"]];
const hlColours = (p) => [p.pnote?.row_hl, p.pnote?.last?.hl, ...Object.values(p.pnote?.cell_hl || {})].filter(Boolean);
const noteMatch = (p, f) => {
  if (!f) return true;
  if (f === "notes") return (p.pnote?.count || 0) > 0;
  if (f === "none") return !(p.pnote?.count > 0);
  if (f === "hl") return hlColours(p).length > 0;
  return hlColours(p).includes(f);
};

export default function PendingWorklistPage() {
  const [sp, setSp] = useSearchParams();
  const bucket = (sp.get("bucket") || "").toUpperCase();
  const age = sp.get("age") || "", who = sp.get("who") || "", user = sp.get("user") || "";
  const [canReport, setCanReport] = useState(null);
  useEffect(() => {
    axios.get(`${API}/order-register/pp/status`, { headers: getAuthHeader() })
      .then(r => setCanReport(!!r.data.can_report)).catch(() => setCanReport(false));
  }, []);
  // users without the Pending by Service right always get their own (scope-limited) view
  const pf = age ? psrParams({ age, user }) : canReport === false ? psrParams({ age: "total" }) : {};
  const nf = sp.get("note") || "";
  const qf = sp.get("query") || "";
  const rf = sp.get("reason") || "";
  const setParam = (k, v) => setSp((prev) => { const n = new URLSearchParams(prev); if (v) n.set(k, v); else n.delete(k); return n; });
  const [buckets, setBuckets] = useState([]);
  const [parcels, setParcels] = useState(null);
  const [q, setQ] = useState("");
  const [at, setAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (canReport === null) return;
    setBusy(true);
    try {
      const [all, one] = await Promise.all([
        axios.get(`${API}/order-register/pending-service-report`, { params: pf, headers: getAuthHeader() }),
        bucket ? axios.get(`${API}/order-register/pending-service-report`, { params: { bucket, ...pf }, headers: getAuthHeader() }) : null]);
      setBuckets(all.data.buckets || []);
      setParcels(one ? one.data.parcels : []);
      setAt(new Date());
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load worklist"); } finally { setBusy(false); }
  }, [bucket, age, user, canReport]);
  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS); return () => clearInterval(t); }, [load]);
  useEffect(() => { document.title = `Pending · ${bucket || "Worklist"}`; }, [bucket]);

  const searched = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!parcels) return parcels;
    return parcels.filter((p) => !s || [p.code, p.customer_name, p.country, p.tracking, p.invoice_no, p.reason, p.pnote?.last?.text,
      ...p.products.map((x) => x.medicine)].some((v) => (v || "").toLowerCase().includes(s)));
  }, [parcels, q]);
  const shown = useMemo(() => searched && applyQueryFilter(searched.filter((p) => noteMatch(p, nf) && reasonMatch(p, rf)), qf), [searched, nf, qf, rf]);
  const counts = useMemo(() => Object.fromEntries(NOTE_FILTERS.map(([v]) =>
    [v, searched ? searched.filter((p) => noteMatch(p, v)).length : null])), [searched]);
  const openInRegister = (p) => openPage(`/dashboard?panel=order_register&or_search=${encodeURIComponent(p.tracking || p.customer_name)}`);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 pt-0 sm:pt-0" data-testid="pending-worklist-page">
      <BackBar title="Pending worklist" />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h1 className="text-xl font-bold text-slate-900" data-testid="pw-title">{bucket === "ALL" ? (canReport && !user ? "All services" : "All my services") : bucket || "Pending worklist"} — pending parcels</h1>
        {age && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid="pw-filter-tag">
          {AGE_LABEL[age] || age}{who ? ` · ${who}` : ""}
          <button type="button" className="ml-1 text-amber-600 hover:text-amber-900" title="Show full bucket" data-testid="pw-filter-clear"
            onClick={() => { const n = new URLSearchParams(sp); ["age", "user", "who"].forEach(k => n.delete(k)); setSp(n); }}>×</button></span>}
        <select value={bucket} onChange={(e) => setParam("bucket", e.target.value)} className="h-8 rounded border px-2 text-xs" data-testid="pw-bucket-select">
          {!bucket && <option value="">Choose a service…</option>}
          <option value="ALL" data-testid="pw-bucket-all">{canReport && !user ? "All services" : "All my services"} ({buckets.reduce((n, b) => n + b.parcels, 0)})</option>
          {buckets.map((b) => <option key={b.bucket} value={b.bucket}>{b.bucket} ({b.parcels})</option>)}
        </select>
        <div className="relative">
          <Search className="absolute left-2 top-2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, code, tracking, product, note…"
            className="h-8 w-[calc(100vw-2rem)] sm:w-72 rounded border pl-8 pr-2 text-xs" data-testid="pw-search" />
        </div>
        <select value={nf} onChange={(e) => setParam("note", e.target.value)} data-testid="pw-note-filter"
          className={`h-8 rounded border px-2 text-xs ${nf ? "border-amber-400 bg-amber-50 font-semibold text-amber-800" : ""}`}>
          {NOTE_FILTERS.map(([v, l]) => <option key={v} value={v} data-testid={`pw-note-opt-${v || "all"}`}>
            {counts[v] != null ? `${l} (${counts[v]})` : l}</option>)}
        </select>
        <QueryFilterSelect value={qf} onChange={(v) => setParam("query", v)} parcels={searched} testId="pw-query-filter" />
        <ReasonFilterSelect value={rf} onChange={(v) => setParam("reason", v)} parcels={searched} testId="pw-reason-filter" />
        <span className="text-xs text-slate-600" data-testid="pw-count">{shown ? `${shown.length} parcel${shown.length === 1 ? "" : "s"}` : ""}</span>
        <button type="button" onClick={load} data-testid="pw-refresh"
          className="ml-auto inline-flex items-center gap-1 rounded-md border bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
        <span className="text-[11px] text-slate-400" data-testid="pw-updated">{at ? `Auto-refresh every minute · updated ${at.toLocaleTimeString()}` : ""}</span>
      </div>
      <p className="mb-2 text-[11px] text-slate-500 md:hidden">Oldest first. Tap "Open" on a parcel to see it in the Order Register.</p>
      <p className="mb-2 text-[11px] text-slate-500 hidden md:block">Oldest first. Select / copy text freely — double-click a row to open it in the Order Register (new tab), filtered to that tracking.</p>
      {!shown ? <div className="py-20 text-center"><Loader2 className="mx-auto w-6 h-6 animate-spin" /></div>
        : <ParcelTable parcels={shown} tall onChanged={load} onRowClick={openInRegister} showService={bucket === "ALL"} />}
      <ParcelQueryDialog />
    </div>
  );
}
